export interface BandLabAuthSession {
    readonly accessToken: string;
    readonly refreshToken: string | null;
    readonly expiresAt: string | null;
    readonly userId: string;
    readonly displayName: string;
    readonly email: string | null;
    readonly handle: string;
    readonly pictureUrl: string | null;
}

interface TokenResponse {
    readonly access_token: string;
    readonly refresh_token?: string;
    readonly expires_in?: number;
}

interface UserInfoResponse {
    readonly sub?: string;
    readonly name?: string;
    readonly email?: string;
    readonly preferred_username?: string;
    readonly nickname?: string;
    readonly picture?: string;
}

interface CallbackResult {
    readonly success: boolean;
    readonly session?: BandLabAuthSession;
    readonly redirectPath?: string;
    readonly error?: string;
}

const authStorageKey = "livestreaming-bandlab-session";
const oauthStateStorageKey = "livestreaming-bandlab-oauth-state";
const oauthVerifierStorageKey = "livestreaming-bandlab-pkce-verifier";
const oauthRedirectStorageKey = "livestreaming-bandlab-oauth-redirect";
const userInfoScope = "openid profile email linked_accounts offline_access";
const bandLabClientId = "bandlab_web";

export class BandLabAuthStore {
    private readonly accountsBaseUrl: string;
    private readonly redirectUri: string;
    private readonly resource: string | null;

    constructor(accountsBaseUrl: string, redirectUri?: string, resource?: string) {
        this.accountsBaseUrl = accountsBaseUrl.replace(/\/$/, "");
        this.redirectUri = sanitizeRedirectUri(redirectUri) ?? getDefaultRedirectUri();
        this.resource = resource?.trim() || null;
    }

    public load(): BandLabAuthSession | null {
        const rawSession = window.localStorage.getItem(authStorageKey);
        if (!rawSession) {
            return null;
        }

        try {
            const parsed = JSON.parse(rawSession) as Partial<BandLabAuthSession>;
            if (!parsed.accessToken || !parsed.userId || !parsed.displayName || !parsed.handle) {
                return null;
            }

            return {
                accessToken: parsed.accessToken,
                refreshToken: parsed.refreshToken ?? null,
                expiresAt: parsed.expiresAt ?? null,
                userId: parsed.userId,
                displayName: parsed.displayName,
                email: parsed.email ?? null,
                handle: parsed.handle,
                pictureUrl: parsed.pictureUrl ?? null,
            };
        } catch {
            return null;
        }
    }

    public save(session: BandLabAuthSession): void {
        window.localStorage.setItem(authStorageKey, JSON.stringify(session));
    }

    public clear(): void {
        window.localStorage.removeItem(authStorageKey);
        window.sessionStorage.removeItem(oauthStateStorageKey);
        window.sessionStorage.removeItem(oauthVerifierStorageKey);
        window.sessionStorage.removeItem(oauthRedirectStorageKey);
    }

    public getAccessToken(): string | null {
        return this.load()?.accessToken ?? null;
    }

    public isAuthenticated(): boolean {
        return Boolean(this.getAccessToken());
    }

    public isCallbackRoute(): boolean {
        return window.location.pathname === "/auth/callback";
    }

    public async startLogin(): Promise<void> {
        const state = buildOpaqueState();
        const codeVerifier = createRandomHex(64);
        const codeChallenge = await createPkceChallenge(codeVerifier);
        const currentPath = `${window.location.pathname}${window.location.search}`;
        const authorizeUrl = new URL(`${this.accountsBaseUrl}/oauth/connect/authorize/callback`);

        authorizeUrl.searchParams.set("client_id", bandLabClientId);
        authorizeUrl.searchParams.set("scope", userInfoScope);
        authorizeUrl.searchParams.set("response_type", "code");
        authorizeUrl.searchParams.set("response_mode", "fragment");
        authorizeUrl.searchParams.set("redirect_uri", this.redirectUri);
        authorizeUrl.searchParams.set("nonce", createRandomHex(16));
        authorizeUrl.searchParams.set("state", state);
        authorizeUrl.searchParams.set("code_challenge", codeChallenge);
        authorizeUrl.searchParams.set("code_challenge_method", "S256");
        if (this.resource) {
            authorizeUrl.searchParams.set("resource", this.resource);
        }

        window.sessionStorage.setItem(oauthStateStorageKey, state);
        window.sessionStorage.setItem(oauthVerifierStorageKey, codeVerifier);
        window.sessionStorage.setItem(oauthRedirectStorageKey, currentPath || "/");

        const loginUrl = new URL(`${this.accountsBaseUrl}/login`);
        loginUrl.searchParams.set("ReturnUrl", authorizeUrl.toString());

        window.location.assign(loginUrl.toString());
    }

    public async handleCallback(): Promise<CallbackResult> {
        const callbackParams = readCallbackParams();
        if (callbackParams.error) {
            return {
                success: false,
                error: callbackParams.errorDescription ?? callbackParams.error,
            };
        }

        if (!callbackParams.code || !callbackParams.state) {
            return {
                success: false,
                error: "Missing code or state in callback",
            };
        }

        const expectedState = window.sessionStorage.getItem(oauthStateStorageKey);
        if (!expectedState || callbackParams.state !== expectedState) {
            return {
                success: false,
                error: "State mismatch during BandLab sign-in",
            };
        }

        const codeVerifier = window.sessionStorage.getItem(oauthVerifierStorageKey);
        if (!codeVerifier) {
            return {
                success: false,
                error: "Missing PKCE code verifier",
            };
        }

        try {
            const tokenResponse = await this.exchangeCodeForToken(callbackParams.code, codeVerifier);
            const userInfo = await this.getUserInfo(tokenResponse.access_token);
            const session = buildSession(tokenResponse, userInfo);
            const redirectPath = window.sessionStorage.getItem(oauthRedirectStorageKey) ?? "/";

            this.save(session);
            window.sessionStorage.removeItem(oauthStateStorageKey);
            window.sessionStorage.removeItem(oauthVerifierStorageKey);
            window.sessionStorage.removeItem(oauthRedirectStorageKey);

            return {
                success: true,
                session,
                redirectPath,
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : "Token exchange failed",
            };
        }
    }

    public logout(): void {
        this.clear();
        window.location.assign("/");
    }

    private async exchangeCodeForToken(code: string, codeVerifier: string): Promise<TokenResponse> {
        const tokenResponse = await fetch(`${this.accountsBaseUrl}/oauth/connect/token`, {
            method: "POST",
            headers: {
                "content-type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams({
                grant_type: "authorization_code",
                client_id: bandLabClientId,
                code,
                redirect_uri: this.redirectUri,
                code_verifier: codeVerifier,
                ...(this.resource ? { resource: this.resource } : {}),
            }).toString(),
        });

        if (!tokenResponse.ok) {
            throw new Error(`BandLab token exchange failed: ${tokenResponse.status}`);
        }

        return tokenResponse.json() as Promise<TokenResponse>;
    }

    private async getUserInfo(accessToken: string): Promise<UserInfoResponse> {
        const response = await fetch(`${this.accountsBaseUrl}/oauth/connect/userinfo`, {
            headers: {
                authorization: `Bearer ${accessToken}`,
            },
        });

        if (!response.ok) {
            throw new Error(`BandLab user info request failed: ${response.status}`);
        }

        return response.json() as Promise<UserInfoResponse>;
    }
}

function readCallbackParams(): {
    code: string | null;
    state: string | null;
    error: string | null;
    errorDescription: string | null;
    } {
    const fragment = window.location.hash.startsWith("#")
        ? window.location.hash.slice(1)
        : window.location.hash;
    const fragmentParams = new URLSearchParams(fragment);
    const searchParams = new URLSearchParams(window.location.search);

    return {
        code: fragmentParams.get("code") ?? searchParams.get("code"),
        state: fragmentParams.get("state") ?? searchParams.get("state"),
        error: fragmentParams.get("error") ?? searchParams.get("error"),
        errorDescription: fragmentParams.get("error_description") ?? searchParams.get("error_description"),
    };
}

function buildOpaqueState(): string {
    const payload = {
        ts: Date.now(),
        path: `${window.location.pathname}${window.location.search}`,
        nonce: createRandomHex(16),
    };

    return window.btoa(JSON.stringify(payload));
}

function getDefaultRedirectUri(): string {
    return `${window.location.origin}/auth/callback`;
}

function sanitizeRedirectUri(redirectUri?: string | null): string | null {
    const value = redirectUri?.trim();
    if (!value) {
        return null;
    }

    return value.replace(/\/$/, "");
}

function createRandomHex(length: number): string {
    const bytes = new Uint8Array(length);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}

async function createPkceChallenge(codeVerifier: string): Promise<string> {
    const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier));
    return toBase64Url(digest);
}

function toBase64Url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (const value of bytes) {
        binary += String.fromCharCode(value);
    }

    return window.btoa(binary)
        .replaceAll("+", "-")
        .replaceAll("/", "_")
        .replace(/=+$/u, "");
}

function buildSession(tokenResponse: TokenResponse, userInfo: UserInfoResponse): BandLabAuthSession {
    const userId = userInfo.sub?.trim();
    if (!userId) {
        throw new Error("BandLab user info did not include a subject");
    }

    const email = userInfo.email?.trim() || null;
    const preferredHandle = userInfo.preferred_username?.trim()
        || userInfo.nickname?.trim()
        || email?.split("@")[0]
        || `user-${userId.slice(-12)}`;
    const displayName = userInfo.name?.trim()
        || userInfo.preferred_username?.trim()
        || userInfo.nickname?.trim()
        || email?.split("@")[0]
        || preferredHandle;
    const expiresAt = tokenResponse.expires_in
        ? new Date(Date.now() + (tokenResponse.expires_in * 1000)).toISOString()
        : null;

    return {
        accessToken: tokenResponse.access_token,
        refreshToken: tokenResponse.refresh_token ?? null,
        expiresAt,
        userId,
        displayName,
        email,
        handle: sanitizeHandle(preferredHandle),
        pictureUrl: userInfo.picture?.trim() || null,
    };
}

function sanitizeHandle(rawHandle: string): string {
    const sanitized = rawHandle
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]+/gu, "-")
        .replace(/-{2,}/gu, "-")
        .replace(/^-+|-+$/gu, "");

    if (sanitized.length >= 3) {
        return sanitized.slice(0, 30);
    }

    return `user-${createRandomHex(4)}`;
}
